import { Link } from 'react-router-dom';
import { breadcrumbLd, type BreadcrumbItem } from './jsonld';
import SeoHead from './SeoHead';
import { buildCanonical } from './config';

interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
  pageTitle: string;
  pageDescription?: string;
  noindex?: boolean;
  extraSchemas?: Record<string, unknown>[];
}

export default function Breadcrumbs({
  items,
  pageTitle,
  pageDescription,
  noindex = false,
  extraSchemas = [],
}: BreadcrumbsProps) {
  const ldItems: BreadcrumbItem[] = items
    .filter((i) => !!i.to)
    .map((i) => ({ name: i.label, url: buildCanonical(i.to!) }));

  const last = items[items.length - 1];
  const lastItem = last?.to
    ? { name: last.label, url: buildCanonical(last.to) }
    : { name: last.label, url: buildCanonical(items.find((i) => i.to)?.to ?? '/') };

  const crumbs = ldItems.length > 0 ? ldItems : [lastItem];

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        noindex={noindex}
        schemas={[breadcrumbLd(crumbs), ...extraSchemas]}
      />
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1 text-gray-500">
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            return (
              <li key={`${item.label}-${idx}`} className="flex items-center gap-1">
                {item.to && !isLast ? (
                  <Link to={item.to} className="hover:text-parksy-600">
                    {item.label}
                  </Link>
                ) : (
                  <span className={isLast ? 'text-gray-900 font-medium' : ''}>{item.label}</span>
                )}
                {!isLast && <span className="text-gray-300">/</span>}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
